import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/lib/i18n";
import { notifyByEmail } from "@/lib/notify";
import { pageHead } from "@/lib/seo";

type Message = {
  id: string;
  sender_id: string;
  recipient_id: string;
  book_id: string | null;
  content: string;
  created_at: string;
  read_at: string | null;
};

type ProfileLite = { id: string; full_name: string | null; avatar_url: string | null };

export const Route = createFileRoute("/messages")({
  validateSearch: (s: Record<string, unknown>): { to?: string; book?: string } => ({
    to: typeof s.to === "string" ? s.to : undefined,
    book: typeof s.book === "string" ? s.book : undefined,
  }),
  head: () => pageHead({ path: "/messages", title: "Messages — Book Swap", description: "Chat with neighbours about the books you're borrowing, lending or swapping.", noindex: true }),

  component: Messages,
});

function Messages() {
  const { user, loading: authLoading } = useAuth();
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [messages, setMessages] = useState<Message[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [active, setActive] = useState<{ otherId: string; bookId: string | null } | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const requestedProfileIds = useRef(new Set<string>());

  const fmtTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleTimeString(isAr ? "ar" : "en", { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  useEffect(() => { if (!authLoading && !user) navigate({ to: "/login" }); }, [authLoading, user, navigate]);

  // Load all messages involving the user
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase.from("messages")
      .select("*")
      .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
      .order("created_at", { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { console.error(error); return; }
        setMessages((data ?? []) as Message[]);
      });

    const channel = supabase
      .channel(`messages-${user.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, (payload) => {
        const m = payload.new as Message;
        if (m.sender_id !== user.id && m.recipient_id !== user.id) return;
        setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
      })
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, [user]);

  // Conversations grouped by the other user
  const conversations = useMemo(() => {
    if (!user) return [] as { otherId: string; lastMessage: Message; unread: number }[];
    const map = new Map<string, { lastMessage: Message; unread: number }>();
    for (const m of messages) {
      const otherId = m.sender_id === user.id ? m.recipient_id : m.sender_id;
      const entry = map.get(otherId);
      const unread = m.recipient_id === user.id && !m.read_at ? 1 : 0;
      if (!entry || new Date(m.created_at) > new Date(entry.lastMessage.created_at)) {
        map.set(otherId, { lastMessage: m, unread: (entry?.unread ?? 0) + unread });
      } else {
        entry.unread += unread;
      }
    }
    return Array.from(map.entries())
      .map(([otherId, v]) => ({ otherId, ...v }))
      .sort((a, b) => +new Date(b.lastMessage.created_at) - +new Date(a.lastMessage.created_at));
  }, [messages, user]);

  // Fetch profile names for everyone we talk to (and the active other if not yet present)
  useEffect(() => {
    const ids = new Set<string>(conversations.map((c) => c.otherId));
    if (active?.otherId) ids.add(active.otherId);
    const missing = Array.from(ids).filter((id) => !profiles[id] && !requestedProfileIds.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => requestedProfileIds.current.add(id));
    supabase.from("public_profiles").select("id, full_name, avatar_url").in("id", missing)
      .then(({ data, error }) => {
        if (error) {
          console.error(error);
          missing.forEach((id) => requestedProfileIds.current.delete(id));
          return;
        }
        if (!data) return;
        setProfiles((prev) => {
          const next = { ...prev };
          for (const p of data as ProfileLite[]) next[p.id] = p;
          return next;
        });
      });
  }, [conversations, active, profiles]);

  // Open conversation from query params
  useEffect(() => {
    if (!user) return;
    setActive((current) => {
      if (search.to && search.to !== user.id) {
        const bookId = search.book ?? null;
        return current && current.otherId === search.to && current.bookId === bookId
          ? current
          : { otherId: search.to, bookId };
      }
      if (!current && conversations.length > 0) {
        return { otherId: conversations[0].otherId, bookId: null };
      }
      return current;
    });
  }, [search.to, search.book, user, conversations]);

  const thread = useMemo(() => {
    if (!user || !active) return [] as Message[];
    return messages.filter((m) =>
      (m.sender_id === user.id && m.recipient_id === active.otherId) ||
      (m.sender_id === active.otherId && m.recipient_id === user.id)
    );
  }, [messages, user, active]);

  // Mark thread as read
  useEffect(() => {
    if (!user || !active) return;
    const unreadIds = thread.filter((m) => m.recipient_id === user.id && !m.read_at).map((m) => m.id);
    if (unreadIds.length === 0) return;
    supabase.from("messages").update({ read_at: new Date().toISOString() }).in("id", unreadIds).then();
  }, [thread, user, active]);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [thread.length, active?.otherId]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !active) return;
    const content = draft.trim();
    if (!content) return;
    setSending(true);
    const { data: created, error } = await supabase.from("messages").insert({
      sender_id: user.id,
      recipient_id: active.otherId,
      book_id: active.bookId,
      content,
    }).select("id").maybeSingle();
    setSending(false);
    if (error) { console.error(error); return toast.error("Could not send message."); }
    if (created?.id) notifyByEmail({ kind: "new-message", messageId: created.id });
    setDraft("");
  }

  if (authLoading || !user) return <div className="min-h-screen bg-background"><Navbar /></div>;

  const otherName = active ? profiles[active.otherId]?.full_name ?? (isAr ? "جار" : "Neighbor") : "";

  return (
    <div className="min-h-screen bg-background" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-8">
        <h1 className="text-3xl font-bold">{isAr ? "الرسائل" : "Messages"}</h1>
        <div className="mt-6 grid gap-4 md:grid-cols-[260px_1fr]">
          <aside className="rounded-2xl bg-card p-2 shadow-[var(--shadow-card)]">
            {conversations.length === 0 && !active ? (
              <p className="p-4 text-sm text-muted-foreground">No conversations yet. Open a book and tap "Message owner".</p>
            ) : (
              <ul className="space-y-1">
                {conversations.map((c) => {
                  const p = profiles[c.otherId];
                  const isActive = active?.otherId === c.otherId;
                  return (
                    <li key={c.otherId}>
                      <button
                        onClick={() => setActive({ otherId: c.otherId, bookId: null })}
                        className={`w-full rounded-lg px-3 py-2 text-left transition-colors ${isActive ? "bg-primary-soft" : "hover:bg-muted"}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate font-medium">{p?.full_name ?? "Neighbor"}</span>
                          {c.unread > 0 && <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">{c.unread}</span>}
                        </div>
                        <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{c.lastMessage.content}</p>
                      </button>
                    </li>
                  );
                })}
                {active && !conversations.some((c) => c.otherId === active.otherId) && (
                  <li>
                    <div className="rounded-lg bg-primary-soft px-3 py-2">
                      <span className="font-medium">{profiles[active.otherId]?.full_name ?? "Neighbor"}</span>
                      <p className="mt-0.5 text-xs text-muted-foreground">New conversation</p>
                    </div>
                  </li>
                )}
              </ul>
            )}
          </aside>

          <section className="flex h-[60vh] flex-col rounded-2xl bg-card shadow-[var(--shadow-card)]">
            {!active ? (
              <div className="flex flex-1 flex-col items-center justify-center text-center text-muted-foreground">
                <MessageCircle className="h-10 w-10 text-primary/40" />
                <p className="mt-3 text-sm">Pick a conversation to start chatting.</p>
              </div>
            ) : (
              <>
                <header className="flex items-center gap-3 border-b border-border px-4 py-3">
                  {profiles[active.otherId]?.avatar_url ? (
                    <img src={profiles[active.otherId]!.avatar_url!} alt={otherName} className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-soft text-sm font-semibold text-primary">
                      {otherName?.[0]?.toUpperCase() ?? "?"}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{otherName}</p>
                    <p className="text-[11px] text-muted-foreground">{isAr ? "محادثة مباشرة" : "Live conversation"}</p>
                  </div>
                </header>
                <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-4">
                  {thread.length === 0 ? (
                    <p className="mt-8 text-center text-sm text-muted-foreground">Say hi 👋</p>
                  ) : (
                    thread.map((m) => {
                      const mine = m.sender_id === user.id;
                      return (
                        <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                          {!mine && (
                            <span className="mb-0.5 px-1 text-[10px] font-medium text-muted-foreground">{otherName}</span>
                          )}
                          <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                            {m.content}
                          </div>
                          <span className="mt-0.5 text-[10px] text-muted-foreground">{fmtTime(m.created_at)}</span>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="border-t border-border px-3 pt-2">
                  <div className="flex flex-wrap gap-1.5">
                    {(isAr
                      ? ["هذا الأسبوع؟", "الأسبوع القادم؟", "اقترح مكانًا", "يبدو جيدًا!"]
                      : ["This weekend?", "Next week?", "Suggest a place", "Sounds good!"]
                    ).map((chip) => (
                      <button
                        key={chip}
                        type="button"
                        onClick={() => setDraft((d) => (d ? `${d} ${chip}` : chip))}
                        className="rounded-full border border-border bg-muted px-3 py-1 text-xs text-foreground/80 transition-colors hover:bg-primary-soft hover:text-primary"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
                <form onSubmit={send} className="flex gap-2 border-t border-border p-3">
                  <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={isAr ? "اكتب رسالة…" : "Write a message…"} maxLength={2000} />
                  <Button type="submit" disabled={sending || !draft.trim()} className="bg-primary hover:bg-primary/90">
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
